import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  Divider,
  Link,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Google,
  Facebook,
  Close,
} from '@mui/icons-material';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';

// Styled Components
const LoginContainer = styled(Paper)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
  backdropFilter: 'blur(5px)',
  WebkitBackdropFilter: 'blur(5px)',
  '@media (max-width: 600px)': {
    padding: '16px',
  },
});

const LoginCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '20px',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
  maxWidth: '450px',
  width: '90%',
  position: 'relative',
  overflow: 'visible',
  zIndex: 1,
  pointerEvents: 'auto',
  '@media (max-width: 600px)': {
    width: '100%',
    maxWidth: '100%',
    margin: '0',
  },
});

const LogoContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '30px',
  position: 'relative',
  top: '-30px',
});

const LogoImage = styled('img')({
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  border: '4px solid #2e5a33',
  backgroundColor: '#fff',
  padding: '5px',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.3)',
});

const StyledTextField = styled(TextField)({
  marginBottom: '20px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fafafa',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#2e5a33',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2e5a33',
    },
  },
});

const LoginButton = styled(Button)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  borderRadius: '12px',
  padding: '12px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.3)',
  cursor: 'pointer',
  touchAction: 'manipulation',
  WebkitTapHighlightColor: 'transparent',
  '&:hover': {
    background: 'linear-gradient(135deg, #3d7342 0%, #2e5a33 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(46, 90, 51, 0.4)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
});

const SocialButton = styled(Button)({
  borderRadius: '12px',
  padding: '10px',
  textTransform: 'none',
  fontWeight: 500,
  border: '2px solid #e0e0e0',
  backgroundColor: '#fff',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    transform: 'translateY(-1px)',
  },
});

const GoogleButton = styled(Button)({
  borderRadius: '12px',
  padding: '12px 24px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  border: '2px solid #e0e0e0',
  backgroundColor: '#fff',
  color: '#444',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  cursor: 'pointer',
  position: 'relative',
  zIndex: 1,
  touchAction: 'manipulation',
  WebkitTapHighlightColor: 'transparent',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    borderColor: '#4285f4',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(66, 133, 244, 0.25)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
});

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: '15px',
  right: '15px',
  color: '#666',
  '&:hover': {
    color: '#2e5a33',
    backgroundColor: 'rgba(46, 90, 51, 0.1)',
  },
});

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  onSwitchToRegister: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ open, onClose, onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetStep, setResetStep] = useState<'email' | 'code' | 'password'>('email');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { login, loginWithGoogle } = useAuth();

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(''); // Limpiar error al escribir
  };

  const validateEmail = (email: string): string => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'El correo electrónico es requerido';
    if (!emailRegex.test(email)) return 'Por favor ingresa un correo electrónico válido';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validaciones
    const emailError = validateEmail(formData.email);
    if (emailError) {
      setError(emailError);
      return;
    }
    
    if (!formData.password) {
      setError('La contraseña es requerida');
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await login(formData.email, formData.password);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setLoading(true);
    setError('');
    
    try {
      if (credentialResponse.credential) {
        await loginWithGoogle(credentialResponse.credential);
        onClose();
      } else {
        setError('No se recibió el token de Google');
      }
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión con Google');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Error al iniciar sesión con Google. Por favor intenta de nuevo.');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const emailError = validateEmail(resetEmail);
    if (emailError) {
      setError(emailError);
      return;
    }

    setLoading(true);
    try {
      // Obtener la URL del backend
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';
      const url = `${API_URL}/auth/send-reset-code/`;
      
      console.log('🔄 Enviando código de recuperación a:', resetEmail);
      console.log('🌐 URL:', url);
      
      // Enviar código de verificación al email
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      console.log('📡 Respuesta status:', response.status);
      
      const data = await response.json();
      console.log('📡 Respuesta data:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Error al enviar el código de verificación');
      }

      console.log('✅ Código enviado exitosamente');
      setResetStep('code');
      setError('');
    } catch (err: any) {
      console.error('❌ Error:', err);
      setError(err.message || 'Error al enviar el código de verificación');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Por favor ingresa un código válido de 6 dígitos');
      return;
    }

    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';
      const url = `${API_URL}/auth/verify-reset-code/`;
      
      console.log('🔄 Verificando código:', verificationCode);
      console.log('🌐 URL:', url);
      
      // Verificar el código
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: resetEmail,
          code: verificationCode 
        }),
      });

      const data = await response.json();
      console.log('📡 Respuesta:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Código inválido o expirado');
      }

      console.log('✅ Código verificado');
      setResetStep('password');
      setError('');
    } catch (err: any) {
      console.error('❌ Error:', err);
      setError(err.message || 'Código inválido o expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!newPassword || newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';
      const url = `${API_URL}/auth/reset-password/`;
      
      console.log('🔄 Restableciendo contraseña');
      console.log('🌐 URL:', url);
      
      // Cambiar la contraseña
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: resetEmail,
          code: verificationCode,
          new_password: newPassword 
        }),
      });

      const data = await response.json();
      console.log('📡 Respuesta:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Error al restablecer la contraseña');
      }

      console.log('✅ Contraseña restablecida');
      setResetSuccess(true);
      setError('');
      
      // Volver al login después de 3 segundos
      setTimeout(() => {
        handleBackToLogin();
      }, 3000);
    } catch (err: any) {
      console.error('❌ Error:', err);
      setError(err.message || 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setForgotPasswordMode(false);
    setResetEmail('');
    setResetSuccess(false);
    setResetStep('email');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  if (!open) return null;

  return (
    <LoginContainer onClick={(e) => e.target === e.currentTarget && onClose()}>
      <LoginCard>
        <CloseButton onClick={onClose}>
          <Close />
        </CloseButton>
        
        <CardContent sx={{ padding: '40px', paddingTop: '20px' }}>
          <LogoContainer>
            <LogoImage
              src="/assets/logos/logo-el-pinon-nuevo.png"
              alt="El Piñón - Hacienda Las Araucarias"
            />
            <Typography
              variant="h4"
              component="h2"
              sx={{
                color: '#2e5a33',
                fontFamily: "'Playfair Display', serif",
                fontWeight: 700,
                marginTop: '15px',
                textAlign: 'center',
              }}
            >
              {forgotPasswordMode 
                ? (resetStep === 'email' ? 'Recuperar Contraseña' 
                   : resetStep === 'code' ? 'Verificar Código' 
                   : 'Nueva Contraseña') 
                : 'Bienvenido'
              }
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                textAlign: 'center',
                marginTop: '5px',
              }}
            >
              {forgotPasswordMode 
                ? (resetStep === 'email' ? 'Ingresa tu correo para recibir un código' 
                   : resetStep === 'code' ? `Código enviado a ${resetEmail}` 
                   : 'Ingresa tu nueva contraseña') 
                : 'Ingresa a tu cuenta de El Piñón'
              }
            </Typography>
          </LogoContainer>

          {error && (
            <Alert severity="error" sx={{ marginBottom: '20px', borderRadius: '12px' }}>
              {error}
            </Alert>
          )}

          {resetSuccess && (
            <Alert severity="success" sx={{ marginBottom: '20px', borderRadius: '12px' }}>
              ¡Contraseña restablecida con éxito! Redirigiendo al inicio de sesión...
            </Alert>
          )}

          {forgotPasswordMode ? (
            // Formulario de recuperación de contraseña
            <>
              {resetStep === 'email' && (
                <form onSubmit={handleForgotPassword}>
                  <StyledTextField
                    fullWidth
                    type="email"
                    label="Correo electrónico"
                    value={resetEmail}
                    onChange={(e) => {
                      setResetEmail(e.target.value);
                      setError('');
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                    }}
                    disabled={loading}
                  />

                  <LoginButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{ marginBottom: '15px' }}
                  >
                    {loading ? 'Enviando código...' : 'Enviar código de verificación'}
                  </LoginButton>

                  <Button
                    fullWidth
                    onClick={handleBackToLogin}
                    sx={{
                      color: '#2e5a33',
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: 'rgba(46, 90, 51, 0.05)',
                      },
                    }}
                  >
                    Volver al inicio de sesión
                  </Button>
                </form>
              )}

              {resetStep === 'code' && (
                <form onSubmit={handleVerifyCode}>
                  <Alert severity="info" sx={{ marginBottom: '20px', borderRadius: '12px' }}>
                    Hemos enviado un código de 6 dígitos a tu correo electrónico.
                  </Alert>

                  <StyledTextField
                    fullWidth
                    type="text"
                    label="Código de verificación"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(value);
                      setError('');
                    }}
                    placeholder="000000"
                    inputProps={{
                      maxLength: 6,
                      style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }
                    }}
                    disabled={loading}
                  />

                  <LoginButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading || verificationCode.length !== 6}
                    sx={{ marginBottom: '15px' }}
                  >
                    {loading ? 'Verificando...' : 'Verificar código'}
                  </LoginButton>

                  <Button
                    fullWidth
                    onClick={() => setResetStep('email')}
                    sx={{
                      color: '#2e5a33',
                      textTransform: 'none',
                      fontWeight: 500,
                      marginBottom: '10px',
                      '&:hover': {
                        backgroundColor: 'rgba(46, 90, 51, 0.05)',
                      },
                    }}
                  >
                    Reenviar código
                  </Button>

                  <Button
                    fullWidth
                    onClick={handleBackToLogin}
                    sx={{
                      color: '#666',
                      textTransform: 'none',
                      fontSize: '14px',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      },
                    }}
                  >
                    Cancelar
                  </Button>
                </form>
              )}

              {resetStep === 'password' && !resetSuccess && (
                <form onSubmit={handleResetPassword}>
                  <StyledTextField
                    fullWidth
                    type={showNewPassword ? 'text' : 'password'}
                    label="Nueva contraseña"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setError('');
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    disabled={loading}
                  />

                  <StyledTextField
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirmar contraseña"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError('');
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    disabled={loading}
                  />

                  <LoginButton
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{ marginBottom: '15px' }}
                  >
                    {loading ? 'Restableciendo...' : 'Restablecer contraseña'}
                  </LoginButton>
                </form>
              )}
            </>
          ) : (
            // Formulario de login normal
            <>
              <form onSubmit={handleSubmit}>
                <StyledTextField
                  fullWidth
                  type="email"
                  label="Correo electrónico"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  label="Contraseña"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <LoginButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ marginBottom: '15px' }}
                >
                  {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </LoginButton>
              </form>

              <Box textAlign="center" sx={{ marginBottom: '20px' }}>
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setForgotPasswordMode(true);
                    setError('');
                  }}
                  sx={{
                    color: '#2e5a33',
                    fontSize: '14px',
                    fontWeight: 500,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </Box>
            </>
          )}

          {!forgotPasswordMode && (
            <>
              <Divider sx={{ marginBottom: '20px', color: '#ccc' }}>
                <Typography variant="body2" color="textSecondary">
                  o continúa con
                </Typography>
              </Divider>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '12px', 
            marginBottom: '20px',
            position: 'relative',
            zIndex: 1,
          }}>
            {/* Contenedor para Google Login con estilo personalizado */}
            <Box 
              sx={{ 
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                '& > div': {
                  width: '100% !important',
                  display: 'flex !important',
                  justifyContent: 'center !important',
                },
                '& > div > div': {
                  width: '100% !important',
                },
                '& iframe': {
                  width: '100% !important',
                  maxWidth: '100% !important',
                },
              }}
            >
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="outline"
                size="large"
                text="continue_with"
                shape="rectangular"
              />
            </Box>

            <SocialButton
              fullWidth
              startIcon={<Facebook />}
              disabled={true}
              sx={{
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              Facebook
            </SocialButton>
          </Box>

              <Box
                sx={{
                  textAlign: 'center',
                  paddingTop: '20px',
                  borderTop: '1px solid #e0e0e0',
                  color: '#666',
                  fontSize: '14px',
                }}
              >
                ¿No tienes cuenta?{' '}
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onSwitchToRegister();
                  }}
                  sx={{
                    color: '#2e5a33',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Regístrate aquí
                </Link>
              </Box>
            </>
          )}
        </CardContent>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginModal;